import React, { useState, useEffect } from 'react'
import { Link, useNavigate, useLocation } from 'react-router-dom'
import { useAuth } from '../contexts/AuthContext'
import { User, Settings, LogOut, X, Menu } from 'lucide-react'

const Navbar = () => {
  const [isOpen, setIsOpen] = useState(false)
  const [showUserMenu, setShowUserMenu] = useState(false)
  const { user, logout, isAuthenticated } = useAuth()
  const navigate = useNavigate()
  const location = useLocation()

  // mobiel 2026-09-10 (audit Navbar.jsx:133-142; component rendert momenteel nergens): menu sluiten bij routewissel
  useEffect(() => { setIsOpen(false) }, [location.pathname])


  const handleLogout = () => {
    logout()
    setShowUserMenu(false)
    navigate('/')
  }

  const getUserDisplayName = () => {
    if (user?.role === 'creator') {
      return user.fullName || user.displayName || `${user.firstName} ${user.lastName}`
    }
    return user?.companyName || user?.contactPerson || 'User'
  }

  return (
    <nav className="w-full flex items-center justify-between px-6 py-4 border-b border-black/10 sticky top-0 left-0 right-0 z-50 bg-black/90 backdrop-blur-xl">
      {/* Logo & Brand */}
      <div className="flex items-center">
        <Link to="/" className="text-2xl font-black tracking-tighter">
          UGC<span className="text-emerald-400">4you</span>
        </Link>
      </div>

      {/* Desktop Navigation */}
      <div className="hidden md:flex items-center gap-8">
        <Link
          to="/projects"
          className="hover:text-emerald-400 transition font-medium"
        >
          Opdrachten
        </Link>
        <Link
          to="/creators"
          className="hover:text-emerald-400 transition font-medium"
        >
          Creators
        </Link>
        <Link
          to="/pakketten"
          className="hover:text-emerald-400 transition font-medium"
        >
          Pakketten
        </Link>

        {!isAuthenticated ? (
          <div className="flex items-center gap-4">
            <Link 
              to="/login" 
              className="hover:text-emerald-400 transition"
            >
              Inloggen
            </Link>
            <Link 
              to="/register" 
              className="px-6 py-2 bg-gradient-to-r from-emerald-600 to-purple-600 rounded-full font-semibold hover:from-emerald-500 hover:to-purple-500 transition"
            >
              Start Nu
            </Link>
          </div>
        ) : (
          <div className="flex items-center gap-4">
            <Link 
              to="/dashboard" 
              className="hover:text-emerald-400 transition font-medium"
            >
              Dashboard
            </Link>
            
            {/* User Menu */}
            <div className="relative">
              <button 
                onClick={() => setShowUserMenu(!showUserMenu)}
                className="flex items-center gap-3 hover:text-emerald-400 transition"
              >
                <div className="text-right">
                  <div className="text-sm font-semibold">
                    {user?.role === 'creator' ? user.firstName : user.contactPerson}
                  </div>
                  <div className="text-xs text-white/60">
                    {user?.role === 'creator' ? 'Creator' : user.companyName}
                  </div>
                </div>
                <div className="w-10 h-10 bg-gradient-to-r from-emerald-400 to-purple-600 rounded-full flex items-center justify-center">
                  {user?.profilePictureUrl ? (
                    <img 
                      src={user.profilePictureUrl} 
                      alt="Profile" 
                      className="w-10 h-10 rounded-full object-cover"
                    />
                  ) : (
                    <User className="w-5 h-5 text-white" />
                  )}
                </div>
              </button>
              
              {showUserMenu && (
                <div className="absolute right-0 mt-2 w-48 rounded-2xl bg-gradient-to-br from-neutral-900/95 to-neutral-800/95 border border-black/10 backdrop-blur-xl py-2 z-50">
                  <Link 
                    to="/profile" 
                    className="flex items-center px-4 py-3 text-sm hover:bg-black/5 transition"
                    onClick={() => setShowUserMenu(false)}
                  >
                    <Settings className="w-4 h-4 mr-3" />
                    Profiel
                  </Link>
                  <button 
                    onClick={handleLogout}
                    className="flex items-center w-full px-4 py-3 text-sm hover:bg-black/5 transition"
                  >
                    <LogOut className="w-4 h-4 mr-3" />
                    Uitloggen
                  </button>
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Mobile menu button */}
      <div className="md:hidden flex items-center">
        <button
          onClick={() => setIsOpen(!isOpen)}
          className="p-2 hover:text-emerald-400 transition"
        >
          {isOpen ? (
            <X className="w-6 h-6" />
          ) : (
            <Menu className="w-6 h-6" />
          )}
        </button>
      </div>

      {/* Mobile Navigation */}
      {isOpen && (
        <div className="absolute top-full left-0 right-0 md:hidden max-h-[calc(100dvh-64px)] overflow-y-auto overscroll-contain border-t border-black/10 bg-black/95 backdrop-blur-xl">
          <div className="px-6 py-4 space-y-4">
            <Link 
              to="/projects" 
              className="block py-2 font-medium hover:text-emerald-400 transition"
              onClick={() => setIsOpen(false)}
            >
              Opdrachten
            </Link>
            <Link
              to="/creators"
              className="block py-2 font-medium hover:text-emerald-400 transition"
              onClick={() => setIsOpen(false)}
            >
              Creators
            </Link>
            <Link
              to="/pakketten"
              className="block py-2 font-medium hover:text-emerald-400 transition"
              onClick={() => setIsOpen(false)}
            >
              Pakketten
            </Link>

            {!isAuthenticated ? (
              <>
                <Link 
                  to="/login" 
                  className="block py-2 font-medium hover:text-emerald-400 transition"
                  onClick={() => setIsOpen(false)}
                >
                  Inloggen
                </Link>
                <Link 
                  to="/register" 
                  className="block py-3 px-6 bg-gradient-to-r from-emerald-600 to-purple-600 rounded-full font-semibold hover:from-emerald-500 hover:to-purple-500 transition text-center"
                  onClick={() => setIsOpen(false)}
                >
                  Start Nu
                </Link>
              </>
            ) : (
              <>
                <div className="py-2 text-sm text-white/60 border-t border-black/10 mt-4 pt-4">
                  Ingelogd als {getUserDisplayName()}
                </div>
                <Link 
                  to="/dashboard" 
                  className="block py-2 font-medium hover:text-emerald-400 transition"
                  onClick={() => setIsOpen(false)}
                >
                  Dashboard
                </Link>
                <Link 
                  to="/profile" 
                  className="block py-2 font-medium hover:text-emerald-400 transition"
                  onClick={() => setIsOpen(false)}
                >
                  Profiel
                </Link>
                <button 
                  onClick={() => {
                    handleLogout()
                    setIsOpen(false)
                  }}
                  className="block w-full text-left py-2 font-medium hover:text-emerald-400 transition"
                >
                  Uitloggen
                </button>
              </>
            )}
          </div>
        </div>
      )}
    </nav>
  )
}

export default Navbar